import * as React from 'react';
import { AlertCircle, Camera, ImageIcon, Plus, Video } from 'lucide-react';
import { ChatTeardrop } from '@phosphor-icons/react';
import { cn } from '@relay-towing/tokens';
import {
  photoListCornerRecipe,
  photoListItemRecipe,
  photoListPhotoRecipe,
  photoListRecipe,
  photoPagerRecipe,
  photoPagerSlideRecipe,
  photoStripRecipe,
  photoTileCountRecipe,
  photoTileImageRecipe,
  photoTileOverlayRecipe,
  photoTileRecipe,
} from '../recipes/image.recipe';
import {
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
} from './BottomSheet';
import { Button } from './Button';
import { Icon } from './Icon';
import { Stack } from './Layout';
import { MediaFrame } from './Media';
import { Spinner } from './Spinner';
import { Text } from './Text';

export interface PhotoListItem {
  id: string;
  /** Nothing yet, for a photo still on the phone: the frame's glyph shows. */
  src?: string | null;
  /**
   * `sending` while it uploads from the outbox, `failed` when it did not
   * arrive. Either way the photo is shown, dimmed, with which it is.
   */
  status?: 'sending' | 'failed';
}

interface PhotoTilesProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  photos: PhotoListItem[];
  /** Tapping a photo: open it full size, or retry a failed one. */
  onOpen: (id: string) => void;
  /** Draws an add tile first, which calls this: open the AddPhotoSheet. */
  onAdd?: () => void;
  /** The add tile's words. */
  addLabel?: string;
  /** What each photo is called for a screen reader: "Photo 3 of 8". */
  photoLabel?: (index: number, total: number) => string;
  /**
   * `lg`, the Figma's call photos, 240 high at their own width, for a
   * call's photos. `sm`, square thumbnails that keep to a card's padding,
   * for the photos on one checklist answer.
   */
  size?: 'lg' | 'sm';
  /**
   * A pill on the first photo saying how many there are: "3 Photos". The
   * call detail shows it, since only the first one or two fit on screen.
   */
  countLabel?: string;
}

const defaultLabel = (index: number, total: number) =>
  `Photo ${index + 1} of ${total}`;

function PhotoTiles({
  photos,
  onOpen,
  onAdd,
  addLabel = 'Add photo',
  photoLabel = defaultLabel,
  size = 'lg',
  countLabel,
}: Pick<
  PhotoTilesProps,
  | 'photos'
  | 'onOpen'
  | 'onAdd'
  | 'addLabel'
  | 'photoLabel'
  | 'size'
  | 'countLabel'
>) {
  return (
    <>
      {onAdd ? (
        <button
          type="button"
          onClick={onAdd}
          className={photoTileRecipe({ add: true, size })}
        >
          <Icon icon={Plus} size="md" />
          <Text as="span" variant="caption" tone="inherit">
            {addLabel}
          </Text>
        </button>
      ) : null}
      {photos.map((photo, index) => {
        const label = photoLabel(index, photos.length);
        return (
          <button
            key={photo.id}
            type="button"
            onClick={() => onOpen(photo.id)}
            aria-label={
              photo.status === 'sending'
                ? `${label}, sending`
                : photo.status === 'failed'
                  ? `${label}, not sent`
                  : label
            }
            className={photoTileRecipe({ size })}
          >
            {size === 'lg' && photo.src ? (
              <img
                src={photo.src}
                alt=""
                loading="lazy"
                className={photoTileImageRecipe()}
              />
            ) : (
              <MediaFrame src={photo.src} ratio="1/1" radius="none" />
            )}
            {countLabel && index === 0 ? (
              <span className={photoTileCountRecipe()} aria-hidden="true">
                <Text as="span" variant="caption-strong" tone="inherit">
                  {countLabel}
                </Text>
              </span>
            ) : null}
            {photo.status ? (
              <span className={photoTileOverlayRecipe()} aria-hidden="true">
                {photo.status === 'sending' ? (
                  <Spinner size="sm" />
                ) : (
                  <Icon icon={AlertCircle} size="md" />
                )}
              </span>
            ) : null}
          </button>
        );
      })}
    </>
  );
}

export type PhotoStripProps = PhotoTilesProps;

/**
 * A call's photos as a row that scrolls sideways, each at its own width,
 * with an add tile at the front when asked: the call detail on a phone.
 *
 *   <PhotoStrip photos={photos} countLabel="3 Photos" onOpen={openPhotos} />
 *
 * A photo still sending from the outbox shows dimmed with a spinner, and one
 * that failed with a warning, so a driver can see what has not arrived yet.
 * PhotoList is the same photos, all at once.
 */
export const PhotoStrip = React.forwardRef<HTMLDivElement, PhotoStripProps>(
  function PhotoStrip(
    {
      photos,
      onOpen,
      onAdd,
      addLabel,
      photoLabel,
      size,
      countLabel,
      className,
      ...props
    },
    ref
  ) {
    return (
      <div
        ref={ref}
        role="group"
        aria-label={countLabel ? `Photos, ${countLabel}` : 'Photos'}
        className={cn(photoStripRecipe({ size }), className)}
        {...props}
      >
        <PhotoTiles
          photos={photos}
          onOpen={onOpen}
          onAdd={onAdd}
          addLabel={addLabel}
          photoLabel={photoLabel}
          size={size}
          countLabel={countLabel}
        />
      </div>
    );
  }
);

const MAX_ZOOM = 4;

const fingerGap = (touches: React.TouchList) =>
  Math.hypot(
    touches[0].clientX - touches[1].clientX,
    touches[0].clientY - touches[1].clientY
  );

/**
 * Two fingers on a photo scale it in place, and letting go puts it back:
 * a look closer, not a mode. The Figma's Pinch Zoom note says it: "letting
 * go resets mode to list view".
 */
function usePinchZoom() {
  const [zoom, setZoom] = React.useState<{ id: string; scale: number } | null>(
    null
  );
  const start = React.useRef<number | null>(null);
  // A pinch ends with fingers lifting off the photo, which some browsers
  // report as a tap; that tap must not open it.
  const pinched = React.useRef(false);

  const release = (event: React.TouchEvent) => {
    if (event.touches.length >= 2) return;
    start.current = null;
    setZoom(null);
  };

  const handlers = (id: string) => ({
    onTouchStart: (event: React.TouchEvent) => {
      if (event.touches.length !== 2) return;
      start.current = fingerGap(event.touches);
      pinched.current = true;
      setZoom({ id, scale: 1 });
    },
    onTouchMove: (event: React.TouchEvent) => {
      if (event.touches.length !== 2 || !start.current) return;
      const scale = fingerGap(event.touches) / start.current;
      setZoom({ id, scale: Math.min(MAX_ZOOM, Math.max(1, scale)) });
    },
    onTouchEnd: release,
    onTouchCancel: release,
  });

  /** Whether a tap is a real one, not the end of a pinch. Resets the flag. */
  const tapped = () => {
    const real = !pinched.current;
    pinched.current = false;
    return real;
  };

  return { zoom, handlers, tapped };
}

export interface PhotoListProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  photos: PhotoListItem[];
  /** Tapping a photo: open it on its own, with its comments. */
  onOpen: (id: string) => void;
  /**
   * Puts a round comment button in each photo's corner, which calls this:
   * the quick comment, without leaving the list.
   */
  onComment?: (id: string) => void;
  /** What each photo is called for a screen reader: "Photo 3 of 8". */
  photoLabel?: (index: number, total: number) => string;
}

/**
 * Every photo on a call, one under another at full width: what the call's
 * Photos sheet lists, as the Figma's photos-added sheet draws it.
 *
 *   <PhotoList photos={photos} onOpen={openPhoto} onComment={quickComment} />
 *
 * Pinching a photo zooms it where it is, over the others, which blur; letting
 * go returns it to the list. Tapping opens it on its own.
 */
export const PhotoList = React.forwardRef<HTMLDivElement, PhotoListProps>(
  function PhotoList(
    {
      photos,
      onOpen,
      onComment,
      photoLabel = defaultLabel,
      className,
      ...props
    },
    ref
  ) {
    const { zoom, handlers, tapped } = usePinchZoom();
    return (
      <div
        ref={ref}
        role="group"
        aria-label="Photos"
        className={cn(photoListRecipe(), className)}
        {...props}
      >
        {photos.map((photo, index) => {
          const label = photoLabel(index, photos.length);
          const zooming = zoom?.id === photo.id;
          return (
            <div
              key={photo.id}
              className={photoListItemRecipe({
                pinch: zooming ? 'zooming' : zoom ? 'behind' : 'rest',
              })}
              // The scale follows the fingers, so it cannot be a class.
              style={
                zooming ? { transform: `scale(${zoom.scale})` } : undefined
              }
              {...handlers(photo.id)}
            >
              <button
                type="button"
                onClick={() => tapped() && onOpen(photo.id)}
                aria-label={
                  photo.status === 'sending'
                    ? `${label}, sending`
                    : photo.status === 'failed'
                      ? `${label}, not sent`
                      : label
                }
                className={photoListPhotoRecipe()}
              >
                {/* The whole picture at the list's width, never cropped:
                    a driver photographs damage, and a crop can cut it out. */}
                <MediaFrame
                  src={photo.src}
                  ratio="auto"
                  fit="contain"
                  radius="none"
                />
                {photo.status ? (
                  <span className={photoTileOverlayRecipe()} aria-hidden="true">
                    {photo.status === 'sending' ? (
                      <Spinner size="sm" />
                    ) : (
                      <Icon icon={AlertCircle} size="md" />
                    )}
                  </span>
                ) : null}
              </button>
              {onComment && !zoom ? (
                <span className={photoListCornerRecipe()}>
                  <Button
                    variant="outline"
                    size="icon-lg"
                    pill
                    aria-label={`Comment on ${label.toLowerCase()}`}
                    onClick={() => onComment(photo.id)}
                  >
                    <ChatTeardrop />
                  </Button>
                </span>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  }
);

export interface PhotoPagerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  photos: PhotoListItem[];
  /** The photo showing, by position. */
  index: number;
  /** A swipe settled on another photo. */
  onIndexChange: (index: number) => void;
  /** What each photo is called for a screen reader: "Photo 3 of 8". */
  photoLabel?: (index: number, total: number) => string;
}

/**
 * One photo at a time, swiped left or right to the next: the picture at the
 * top of a photo's own sheet, as the Figma's Isolated frames draw it ("can
 * swipe left/right for other images").
 *
 *   <PhotoPager photos={photos} index={index} onIndexChange={setIndex} />
 *
 * The slides are a row that snaps, so the swipe and its momentum are the
 * browser's own; the pager reports which photo it settled on, and scrolls
 * to `index` when the screen changes it.
 */
export const PhotoPager = React.forwardRef<HTMLDivElement, PhotoPagerProps>(
  function PhotoPager(
    {
      photos,
      index,
      onIndexChange,
      photoLabel = defaultLabel,
      className,
      ...props
    },
    ref
  ) {
    const scroller = React.useRef<HTMLDivElement | null>(null);
    const setRefs = (node: HTMLDivElement | null) => {
      scroller.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    };

    React.useEffect(() => {
      const node = scroller.current;
      if (!node || !node.clientWidth) return;
      if (Math.round(node.scrollLeft / node.clientWidth) === index) return;
      node.scrollTo?.({ left: index * node.clientWidth });
    }, [index]);

    const settle = () => {
      const node = scroller.current;
      if (!node || !node.clientWidth) return;
      const landed = Math.round(node.scrollLeft / node.clientWidth);
      if (landed !== index && landed >= 0 && landed < photos.length)
        onIndexChange(landed);
    };

    return (
      <div
        ref={setRefs}
        role="group"
        aria-roledescription="carousel"
        aria-label="Photos"
        onScroll={settle}
        className={cn(photoPagerRecipe(), className)}
        {...props}
      >
        {photos.map((photo, position) => (
          <div
            key={photo.id}
            role="group"
            aria-roledescription="slide"
            aria-label={photoLabel(position, photos.length)}
            aria-hidden={position !== index}
            className={photoPagerSlideRecipe()}
          >
            {/* Uncropped, as in the list: the photo is the evidence. */}
            <MediaFrame
              src={photo.src}
              alt={photoLabel(position, photos.length)}
              ratio="auto"
              fit="contain"
              radius="xl"
            />
          </div>
        ))}
      </div>
    );
  }
);

export interface AddPhotoSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Open the camera. The app asks the native bridge, or a file input. */
  onTakePhoto: () => void;
  /** Open the photo library. */
  onChooseFromLibrary: () => void;
  /**
   * Add a video. Offered whatever the plan, so a member on a plan without
   * video finds out it exists: the screen opens the upsell for them.
   */
  onAddVideo?: () => void;
  title?: string;
}

/**
 * Where a photo comes from, as the Figma's Add photos sheet asks it: Use
 * camera or From photo library, each a full-width button in a wash, and
 * a video when the screen offers one. Each closes the sheet as it goes.
 *
 *   <AddPhotoSheet open={open} onOpenChange={setOpen}
 *     onTakePhoto={takePhoto} onChooseFromLibrary={chooseFromLibrary} />
 *
 * The app does the picking, through the native bridge in a wrapper or a
 * file input in a browser; the sheet only asks which.
 */
export function AddPhotoSheet({
  open,
  onOpenChange,
  onTakePhoto,
  onChooseFromLibrary,
  onAddVideo,
  title = 'Add photos',
}: AddPhotoSheetProps) {
  const actions = [
    { label: 'Use camera', icon: Camera, run: onTakePhoto },
    { label: 'From photo library', icon: ImageIcon, run: onChooseFromLibrary },
    ...(onAddVideo
      ? [{ label: 'Add a video', icon: Video, run: onAddVideo }]
      : []),
  ];
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title={title}>
        <Stack gap={4}>
          {actions.map((action) => (
            <BottomSheetClose key={action.label} asChild>
              <Button
                variant="secondary"
                size="xl"
                pill
                fullWidth
                leadingIcon={<action.icon />}
                onClick={action.run}
              >
                {action.label}
              </Button>
            </BottomSheetClose>
          ))}
        </Stack>
      </BottomSheetContent>
    </BottomSheet>
  );
}
